import { useState, useCallback, useEffect } from 'react';
import { fetchTerms, addTerm } from '../api/terms';
import { supabase } from '../lib/supabase';
import { getCachedData, invalidateCache } from '../lib/cache';

export const useTerms = () => {
  const [terms, setTerms] = useState([]);
  const [originalTerms, setOriginalTerms] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  // 정렬 함수
  const sortTerms = (terms, sortOption, wrongAnswers) => {
    switch (sortOption) {
      case 'alphabet':
        return [...terms].sort((a, b) => a.term.localeCompare(b.term));
      case 'random':
        return [...terms].sort(() => Math.random() - 0.5);
      case 'wrong_first':
        return [...terms].sort((a, b) => {
          const aIsWrong = wrongAnswers.some(wrong => wrong.termId === a.id);
          const bIsWrong = wrongAnswers.some(wrong => wrong.termId === b.id);
          if (aIsWrong && !bIsWrong) return -1;
          if (!aIsWrong && bIsWrong) return 1;
          return a.term.localeCompare(b.term);
        });
      default:
        return terms;
    }
  };

  // 용어 데이터 로드
  const loadTerms = useCallback(async () => {
    try {
      setLoading(true);
      console.log('📚 용어 데이터 가져오기 시작');
      
      // 현재 세션 확인
      const { data: { session } } = await supabase.auth.getSession();
      console.log('현재 세션 상태:', { 
        로그인_상태: !!session,
        세션_만료: session ? new Date(session.expires_at * 1000).toLocaleString() : '없음'
      });
      
      // 캐시된 데이터 사용
      const data = await getCachedData('terms', async () => {
        console.log('캐시 미스: 데이터베이스에서 용어 데이터 로드 시작');
        
        const { data, error } = await supabase
          .from('terms')
          .select('*')
          .order('subject', { ascending: true })
          .order('term', { ascending: true });

        if (error) {
          console.error('용어 데이터 로드 오류:', error);
          throw error;
        }
        
        console.log('데이터베이스에서 용어 데이터 로드 완료:', { count: data?.length || 0 });
        return data;
      });

      console.log('✅ 용어 데이터 로드 완료:', { count: data?.length || 0 });
      
      setTerms(data || []);
      setOriginalTerms(data || []);
      setError('');
    } catch (err) {
      console.error('❌ 용어 데이터 로드 실패:', err);
      setError(err.message || '용어를 불러오는데 실패했습니다.');
    } finally {
      setLoading(false);
    }
  }, []);

  // 용어 추가
  const handleAddTerm = useCallback(async (newTermData) => {
    try {
      console.log('📝 새 용어 추가 시도:', newTermData);
      await addTerm(newTermData);
      await loadTerms();
      console.log('✅ 용어 추가 성공');
      return true;
    } catch (error) {
      console.error('❌ 용어 추가 실패:', error);
      setError(error.message || '용어 추가에 실패했습니다.');
      return false;
    }
  }, [loadTerms]);

  // Subject별 용어 개수 계산
  const getSubjectCount = useCallback((subject) => {
    return originalTerms.filter(term => term.subject === subject).length;
  }, [originalTerms]);

  const refetch = async () => {
    // 캐시 무효화 후 데이터 다시 로드
    invalidateCache('terms');
    await loadTerms();
  };

  return {
    terms,
    originalTerms,
    loading,
    error,
    sortTerms,
    loadTerms,
    handleAddTerm,
    getSubjectCount,
    refetch
  };
};
